// Where a document opens, and whether its viewer has the footer bar. Both follow the legacy open
// path (openPdfUrl, public/docbox.js:32827-32914) for what the AWS widget opens: document links.
import type { ViewMode } from "../boot/site-options";

/**
 * drawer: the side drawer (DOCBOX_VIEW_MODE "drawer"). window: the modal of the other modes, which
 * fills the browser window (createOverlay, docbox.js:16708). inline: after the clicked link.
 * panel: inside the host's DOCBOX_PANEL_SELECTOR element.
 */
export type Frame = "drawer" | "window" | "inline" | "panel";

export type Placement =
  | { frame: "drawer" | "window" }
  | { frame: "inline"; anchor: Element }
  | { frame: "panel"; mount: Element };

/** docboxViewportIsMobile, docbox.js:14326. */
export function isMobileViewport(win: Window = window): boolean {
  if (!win.matchMedia) return false;
  if (win.matchMedia("(max-width:1024px)").matches) return true;
  // iOS "Request Desktop Website" widens the CSS viewport. Device width and a coarse pointer stay
  // phone-like.
  return (
    win.matchMedia("(max-device-width:1024px)").matches &&
    win.matchMedia("(pointer: coarse)").matches
  );
}

/**
 * The legacy order: the DOCBOX_PANEL_SELECTOR element, then the
 * DOCBOX_HOST_PREVIEW_CONTAINER_SELECTOR element (docbox.js:32842-32871), then inline for
 * "inline", then a modal. A phone or tablet viewport skips both in-page placements and gets the
 * modal (docbox.js:32904). DOCBOX_MOBILE_PANEL_MOUNT keeps the panel elements on that viewport
 * (docboxPreferPanelMountOnViewport, docbox.js:14347), not inline. The modal is the drawer only in
 * drawer mode (docbox.js:16739).
 */
export function choosePlacement({
  mode,
  panelSelector,
  hostContainerSelector = "",
  mobilePanelMount = false,
  trigger,
  win = window,
}: {
  mode: ViewMode;
  panelSelector: string;
  hostContainerSelector?: string;
  mobilePanelMount?: boolean;
  trigger: Element | null;
  win?: Window;
}): Placement {
  const mobile = isMobileViewport(win);
  if (!mobile || mobilePanelMount) {
    for (const selector of [panelSelector, hostContainerSelector]) {
      if (!selector) continue;
      let mount: Element | null = null;
      try {
        mount = win.document.querySelector(selector);
      } catch {
        // An invalid selector finds nothing, as in legacy (queryMountFromDocuments, docbox.js:32174).
      }
      if (mount) return { frame: "panel", mount };
    }
  }
  if (mode === "inline" && !mobile && trigger?.isConnected) {
    return { frame: "inline", anchor: trigger };
  }
  return { frame: mode === "drawer" ? "drawer" : "window" };
}

/**
 * docboxIsCompactEmbedMount, docbox.js:13318: narrower than 94% of the viewport and shorter than
 * 96% of it. An ordinary link is compact.
 */
export function isCompact(el: Element, win: Window = window): boolean {
  const { width, height } = el.getBoundingClientRect();
  const vw = win.innerWidth;
  const vh = win.innerHeight;
  if (!(vw > 0 && vh > 0) || !(width > 0)) return false;
  return width < vw * 0.94 && (height <= 0 || height < vh * 0.96);
}

/**
 * The footer bar shows only in the window modal, and only when every legacy condition allows it.
 * The drawer hides it (docbox.js:14546). An inline or panel viewer never builds it
 * (docbox.js:16890, :16962). The window modal builds it unless docboxShouldOmitShellFooter
 * (docbox.js:13339) omits it: the page is in a frame, a panel selector or a host preview
 * container selector is set (docbox.js:13353-13357), or the clicked element is compact. A modal without the backdrop hides it (shouldHideDocboxFooter,
 * docbox.js:13383), and DOCBOX_MODAL_BACKDROP is false by default. So for an ordinary link the
 * footer stays hidden, as in legacy.
 */
export function showsFooter({
  frame,
  backdrop,
  panelSelector,
  hostContainerSelector = "",
  trigger,
  win = window,
}: {
  frame: Frame;
  backdrop: boolean;
  panelSelector: string;
  hostContainerSelector?: string;
  trigger: Element | null;
  win?: Window;
}): boolean {
  if (frame !== "window" || !backdrop) return false;
  if (panelSelector || hostContainerSelector) return false;
  if (inFrame(win)) return false;
  return !(trigger && isCompact(trigger, win));
}

/** docboxIsIframeHostWindow, docbox.js:13305. */
function inFrame(win: Window): boolean {
  try {
    return win.self !== win.top;
  } catch {
    // A cross-origin parent throws: the page is in a frame.
    return true;
  }
}

/** Ancestors legacy restores: SchoolBlocks preview and terrace wrappers (docbox.js:13448). */
const HOSTISH =
  /filePreview|filesFolders|Terrace|iframeContainer|ThirdPartyIframe|transitionWrapper|ScrollbarsCustom|_BlockCS/i;
/** Where the walk stops: the SchoolBlocks page grid (docbox.js:13445). */
const PAGE_GRID = /sb-main-content|StaticGrid-module|pageBody|MainGridPage/i;

/**
 * docboxUnlockPanelHostInteraction, docbox.js:13403. A SchoolBlocks preview column can keep its
 * reader at opacity 0 with pointer-events none until its own transition ends, or forever when the
 * viewer mounts first. The viewer's host element, and each preview wrapper above it up to the page
 * grid, gets its opacity, pointer events and visibility back.
 */
export function unlockPanelHost(host: HTMLElement): void {
  host.style.setProperty("opacity", "1", "important");
  host.style.setProperty("pointer-events", "auto", "important");
  host.style.setProperty("visibility", "visible", "important");
  const doc = host.ownerDocument;
  const win = doc.defaultView;
  let node: HTMLElement | null = host.parentElement;
  for (let guard = 1; node && guard < 18; guard += 1) {
    if (node === doc.documentElement || node === doc.body) break;
    const cls = node.getAttribute("class") ?? "";
    if (win && HOSTISH.test(cls)) {
      const style = win.getComputedStyle(node);
      if (style.pointerEvents === "none")
        node.style.setProperty("pointer-events", "auto", "important");
      const opacity = parseFloat(style.opacity);
      if (isFinite(opacity) && opacity < 0.05)
        node.style.setProperty("opacity", "1", "important");
      if (style.visibility === "hidden")
        node.style.setProperty("visibility", "visible", "important");
    }
    if (PAGE_GRID.test(cls) || node.id === "sb-main-content") break;
    node = node.parentElement;
  }
}

/** Legacy unlocks again after 250 and 800 ms: CMS transitions can hide the column again. */
export const UNLOCK_RETRY_MS = [250, 800];
